/**
 * Store helpers — pure utility functions used by store actions.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { MultiAccountStateV4 } from '../../accounts'
import { getActiveAccount } from '../../accounts'
import { GroupId, GROUPS } from '../../config/systemConfig'
import { debug } from '../../utils/logger'

// ── Derived State Utilities ──────────────────────────────────────────

/**
 * Derive selectedGroup from context instead of relying on manual sync.
 * Priority: learner.grade → viewingLearner.grade → classroom.groupLevel → manual → default
 *
 * This is the foundation for Fase 2 step 2.12 — gradually replacing
 * the synced selectedGroup state with a computed value.
 *
 * Usage: const group = deriveSelectedGroup(store.getState())
 */
export function deriveSelectedGroup(state: {
  multiAccountState: MultiAccountStateV4 | null
  viewingLearnerId: string | null
  selectedGroup: GroupId
}): GroupId {
  const mas = state.multiAccountState
  if (!mas) return state.selectedGroup

  const activeAccount = getActiveAccount(mas.accounts)
  if (!activeAccount) return state.selectedGroup

  // 1. Active learner → use their grade
  if (activeAccount.role === 'learner' && 'grade' in activeAccount) {
    const grade = (activeAccount as { grade?: number }).grade
    if (grade && GROUPS.includes(grade as GroupId)) {
      return grade as GroupId
    }
  }

  // 2. Viewing a learner (teacher/parent) → use that learner's grade
  if (state.viewingLearnerId) {
    const viewedAccount = mas.accounts.accountsById[state.viewingLearnerId]
    if (viewedAccount?.role === 'learner' && 'grade' in viewedAccount) {
      const grade = (viewedAccount as { grade?: number }).grade
      if (grade && GROUPS.includes(grade as GroupId)) {
        return grade as GroupId
      }
    }
  }

  // 3. Fall back to current selectedGroup (which may be manually set or synced)
  return state.selectedGroup
}

// ── Helper: Remove demo accounts ─────────────────────────────────────

export function removeDemoAccounts(
  state: MultiAccountStateV4,
): MultiAccountStateV4 {
  const demoIds = new Set(
    Object.values(state.accounts.accountsById)
      .filter((a) => a.isDemo)
      .map((a) => a.id),
  )
  if (demoIds.size === 0) return state

  const realAccounts = Object.fromEntries(
    Object.entries(state.accounts.accountsById).filter(
      ([id]) => !demoIds.has(id),
    ),
  )
  const realLearnerData = Object.fromEntries(
    Object.entries(state.learnerData).filter(([id]) => !demoIds.has(id)),
  )
  const filterIds = (ids: string[]) => ids.filter((id) => !demoIds.has(id))
  const cleanRelationships = {
    parentToLearners: Object.fromEntries(
      Object.entries(state.accounts.relationships.parentToLearners)
        .filter(([parentId]) => !demoIds.has(parentId))
        .map(([parentId, learnerIds]) => [parentId, filterIds(learnerIds)])
        .filter(([, ids]) => (ids as string[]).length > 0),
    ),
    learnerToParents: Object.fromEntries(
      Object.entries(state.accounts.relationships.learnerToParents)
        .filter(([learnerId]) => !demoIds.has(learnerId))
        .map(([learnerId, parentIds]) => [learnerId, filterIds(parentIds)])
        .filter(([, ids]) => (ids as string[]).length > 0),
    ),
    teacherToLearners: Object.fromEntries(
      Object.entries(state.accounts.relationships.teacherToLearners)
        .filter(([teacherId]) => !demoIds.has(teacherId))
        .map(([teacherId, learnerIds]) => [teacherId, filterIds(learnerIds)])
        .filter(([, ids]) => (ids as string[]).length > 0),
    ),
    learnerToTeachers: Object.fromEntries(
      Object.entries(state.accounts.relationships.learnerToTeachers ?? {})
        .filter(([learnerId]) => !demoIds.has(learnerId))
        .map(([learnerId, teacherIds]) => [learnerId, filterIds(teacherIds)])
        .filter(([, ids]) => (ids as string[]).length > 0),
    ),
  }
  debug(`🧹 Cleaned up ${demoIds.size} demo accounts`)
  return {
    ...state,
    accounts: {
      ...state.accounts,
      accountsById: realAccounts,
      relationships: cleanRelationships,
    },
    learnerData: realLearnerData,
  }
}
